import { Activity } from "@com.mgmtp.a12.client/client-core/lib/core/activity";

export type DeepLinkRoute =
	| { readonly type: "dashboard" }
	| { readonly type: "fachverfahren" }
	| { readonly type: "fachverfahren-overview"; readonly applicationKey: string }
	| {
			readonly type: "fachverfahren-document";
			readonly applicationKey: string;
			readonly documentId: string;
	  }
	| { readonly type: "textbausteine" }
	| { readonly type: "stammdaten-overview"; readonly applicationKey: string }
	| {
			readonly type: "stammdaten-document";
			readonly applicationKey: string;
			readonly documentId: string;
	  };

const DASHBOARD_SEGMENT = "dashboard";
const FACHVERFAHREN_SEGMENT = "fachverfahren";
const STAMMDATEN_SEGMENT = "stammdaten";
const TEXTBAUSTEINE_SEGMENT = "textbausteine";

export function parseDeepLink(pathname: string, basePath = ""): DeepLinkRoute | undefined {
	const relativePath = removeBasePath(normalizePath(pathname), normalizeBasePath(basePath));
	if (relativePath === undefined) {
		return undefined;
	}

	const segments = relativePath
		.split("/")
		.filter(Boolean)
		.map(segment => decodeURIComponent(segment));

	if (segments.length === 1 && segments[0] === DASHBOARD_SEGMENT) {
		return { type: "dashboard" };
	}
	if (segments.length === 1 && segments[0] === FACHVERFAHREN_SEGMENT) {
		return { type: "fachverfahren" };
	}
	if (segments.length === 2 && segments[0] === FACHVERFAHREN_SEGMENT) {
		return { type: "fachverfahren-overview", applicationKey: segments[1] };
	}
	if (segments.length === 3 && segments[0] === FACHVERFAHREN_SEGMENT) {
		return {
			type: "fachverfahren-document",
			applicationKey: segments[1],
			documentId: segments[2]
		};
	}
	if (segments.length === 2 && segments[0] === STAMMDATEN_SEGMENT && segments[1] === TEXTBAUSTEINE_SEGMENT) {
		return { type: "textbausteine" };
	}
	if (segments.length === 2 && segments[0] === STAMMDATEN_SEGMENT) {
		return { type: "stammdaten-overview", applicationKey: segments[1] };
	}
	if (segments.length === 3 && segments[0] === STAMMDATEN_SEGMENT) {
		return {
			type: "stammdaten-document",
			applicationKey: segments[1],
			documentId: segments[2]
		};
	}

	return undefined;
}

export function formatDeepLink(route: DeepLinkRoute, basePath = ""): string {
	const segments = (() => {
		switch (route.type) {
			case "dashboard":
				return [DASHBOARD_SEGMENT];
			case "fachverfahren":
				return [FACHVERFAHREN_SEGMENT];
			case "fachverfahren-overview":
				return [FACHVERFAHREN_SEGMENT, route.applicationKey];
			case "fachverfahren-document":
				return [FACHVERFAHREN_SEGMENT, route.applicationKey, route.documentId];
			case "textbausteine":
				return [STAMMDATEN_SEGMENT, TEXTBAUSTEINE_SEGMENT];
			case "stammdaten-overview":
				return [STAMMDATEN_SEGMENT, route.applicationKey];
			case "stammdaten-document":
				return [STAMMDATEN_SEGMENT, route.applicationKey, route.documentId];
		}
	})();

	const encodedPath = segments.map(encodeURIComponent).join("/");
	return `${normalizeBasePath(basePath)}/${encodedPath}`;
}

export function getApplicationBasePath(): string {
	const baseElement = document.querySelector("base");
	return baseElement ? normalizeBasePath(new URL(baseElement.href).pathname) : "";
}

export function getDocumentReference(documentId: string, modelId: string): string {
	return documentId.includes("/") ? documentId : `${modelId}/${documentId}`;
}

export function getDocumentId(documentReference: string, modelId: string): string {
	const modelPrefix = `${modelId}/`;
	return documentReference.startsWith(modelPrefix) ? documentReference.slice(modelPrefix.length) : documentReference;
}

export function isNewerActivity(activity: Activity, current: Activity | undefined): boolean {
	return current === undefined || (activity.activationTimestamp ?? 0) >= (current.activationTimestamp ?? 0);
}

function normalizePath(path: string): string {
	const normalized = path.startsWith("/") ? path : `/${path}`;
	return normalized.length > 1 ? normalized.replace(/\/+$/, "") : normalized;
}

function normalizeBasePath(basePath: string): string {
	const normalized = normalizePath(basePath);
	return normalized === "/" ? "" : normalized;
}

function removeBasePath(pathname: string, basePath: string): string | undefined {
	if (!basePath) {
		return pathname;
	}
	if (pathname === basePath) {
		return "/";
	}
	return pathname.startsWith(`${basePath}/`) ? pathname.slice(basePath.length) : undefined;
}
